import {
    Box,
    Container,
    Grid,
    Paper,
    Stack,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import axiosClient from "../../../../../lib/axios";
import DisplayNotFoundInfo from "../../../../../components/DisplayNotFindInfo";
import PermissionGuard from "../../../../../components/PermissionGuard";
import ScreenBanner from "../../../../../components/ScreenBanner";
import useSetParamsURL from "../../../../../lib/useSetParamsURL";
import { ModalDetalheHistoricoRecebimentoProvider, useModalDetalheHistoricoRecebimento } from "../ModalDetalheHistoricoRecebimentoContext";
import useDebounce from "../../../../../lib/useDebounce";
import { formatarValorMonetario } from "../../../../../helpers/masks";
import SearchWithFilters from "../../../../../components/BasicComponents/SearchWithFilters";
import DialogHistoricoEntradaFilter from "./components/DialogHistoricoEntradaFilter";
import { Status } from "./components/Status";
import ChipList from "../../../../../components/BasicComponents/ChipList";
import { getChipFilterItems } from "../../../../../helpers/getChipFilterItems";
import { processUrlFilters } from "../../../../../components/TableDataGridV2/helpers";
import { TFilters } from "../../../../../components/TableDataGridV2/types";

function ListarHistoricoRecebimentoContent() {

    const { openModal } = useModalDetalheHistoricoRecebimento();
    const { setParamsUrl, searchParams, setMultipleParamsUrl } = useSetParamsURL();

    const [filtros, setFiltros] = useState<TFiltroHistoricoRecebimento>({});
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [openFiltros, setOpenFiltros] = useState<boolean>(false);
    const debouncedKeyword = useDebounce(filtros.palavraChave ?? '', 1000);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);


    const queryParams = useMemo(() => {
        const params: Record<string, any> = {
            page: page + 1,
            limit: rowsPerPage,
        };

        if (debouncedKeyword) {
            params.pesquisar = debouncedKeyword;
        }

        if (filtros.status && filtros.status.length > 0) {
            params.status = filtros.status;
        }

        if (filtros.somenteValorOperacao !== undefined) {
            params.somente_valor_operacao = filtros.somenteValorOperacao;
        }

        return params;
    }, [debouncedKeyword, filtros, page, rowsPerPage]);

    const { data, isLoading } = useQuery({
        queryKey: ['pesquisar-historico-mercadorias', queryParams],
        queryFn: () => axiosClient.get("/estoque/entrada-mercadorias/pesquisar-entradas", {
            params: queryParams
        }),
        keepPreviousData: true,
        refetchOnWindowFocus: false
    });

    const { dataHistorico }: { dataHistorico: any[] } = useMemo(() => ({
        dataHistorico: data?.data?.data?.data || []
    }), [data]);

    const total = data?.data?.data?.total || 0;

    const handleChangePage = (_: any, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleApplyFilters = (newFilters: TFiltroHistoricoRecebimento) => {
        const novoObjeto: TFilters = { ...newFilters };
        Object.keys(newFilters).forEach((key) => {
            if (key.startsWith('status')) {
                novoObjeto.status = [novoObjeto.status];
            }
            if (key === 'somenteValorOperacao') {
                novoObjeto.somente_valor_operacao = novoObjeto.somenteValorOperacao;
                delete novoObjeto[key];
            }
            if (key.startsWith('palavraChave')) {
                novoObjeto.pesquisar = novoObjeto.palavraChave;
                delete novoObjeto[key];
            }

        });

        setMultipleParamsUrl(novoObjeto);

        setFiltros(newFilters);
        setPage(0);
    }

    const chipItems = useMemo(() => {
        return getChipFilterItems(
            filtros,
            filtersChipLabels,
            ['palavraChave']
        );
    }, [filtros]);

    const handleDeleteFilter = (item: string) => {
        const newFiltros = { ...filtros };
        const urlFilters = processUrlFilters({}, searchParams);

        if (item.startsWith('Status:')) {
            delete newFiltros.status;
            delete urlFilters.status;
        } else if (item.startsWith('Somente valor da operação:')) {
            delete newFiltros.somenteValorOperacao;
            delete urlFilters.somente_valor_operacao;
        }
        else {
            Object.entries(filtros).forEach(([key, value]) => {
                const label = filtersChipLabels[key as keyof typeof filtersChipLabels] || key;
                const displayValue = Array.isArray(value) ? value.join(', ') : value;
                const chipText = `${label}: ${displayValue}`;

                if (chipText === item) {
                    delete newFiltros[key as keyof TFiltroHistoricoRecebimento];
                }
            });
        }

        setMultipleParamsUrl(urlFilters);
        setFiltros(newFiltros);
    }

    const handleSearch = useCallback((pesquisa: string) => {
        const urlFilters = processUrlFilters({}, searchParams);
        urlFilters.pesquisar = pesquisa || undefined;
        setMultipleParamsUrl(urlFilters);
        setFiltros((prevState) => ({ ...prevState, palavraChave: pesquisa }));
        setPage(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    useEffect(() => {
        const urlFilters = processUrlFilters({}, searchParams);
        if (urlFilters && Object.keys(urlFilters).length > 0) {
            const filtrosFromUrl: TFiltroHistoricoRecebimento = {};
            if (urlFilters.pesquisar) {
                filtrosFromUrl.palavraChave = urlFilters.pesquisar;
            }
            if (urlFilters.status) {
                filtrosFromUrl.status = urlFilters.status;
            }
            if (urlFilters.somente_valor_operacao) {
                filtrosFromUrl.somenteValorOperacao = urlFilters.somente_valor_operacao;
            }

            setFiltros(filtrosFromUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <Box sx={{ background: "#F5F6FA", minHeight: "100vh", py: 4 }}>
                <Container maxWidth="lg">
                    <Typography textAlign="center" variant="h5" mb={3}>
                        Histórico de recebimentos
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }} ref={containerRef}>
                            <Paper elevation={2} sx={{ p: 2 }}>
                                <Box sx={{ width: "100%", overflowX: "auto" }}>
                                    <SearchWithFilters
                                        palavraChave={filtros.palavraChave ?? ''}
                                        textFieldProps={{
                                            inputRef: inputRef,
                                            label: "Pesquisar",
                                            placeholder: "Pesquise por número da NF, emitente ou destinatário",
                                            size: "medium",
                                        }}
                                        pesquisar={handleSearch}
                                        setTermoPesquisa={handleSearch}
                                        handleOpenFilters={() => setOpenFiltros(true)}
                                    />
                                    <ChipList
                                        items={chipItems}
                                        onDelete={handleDeleteFilter}
                                    />

                                    {isLoading ? (
                                        <Box p={3}>
                                            <Typography>Carregando...</Typography>
                                        </Box>
                                    ) : dataHistorico.length === 0 ? (
                                        <DisplayNotFoundInfo label="Nenhum histórico encontrado" />
                                    ) : (
                                        <>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell width="20%"><b>NF-e</b></TableCell>
                                                        <TableCell width="40%"><b>Informações</b></TableCell>
                                                        <TableCell width="20%" align="right"><b>Valor Total</b></TableCell>
                                                        <TableCell width="20%" align="right"><b>Status</b></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {dataHistorico.map((row) => (
                                                        <TableRow key={row.id}>
                                                            <TableCell>
                                                                <Box onClick={() => {
                                                                    setParamsUrl(row.id.toString(), "detalhe-historico-recebimento");
                                                                    openModal(row.id.toString());
                                                                }}>
                                                                    <Typography variant="subtitle1" fontWeight="medium" sx={{ color: "primary.main", fontWeight: "bold", textDecoration: "underline", cursor: "pointer" }}>
                                                                        NF{row.nf_numero}{row.nf_serie ? `/${row.nf_serie}` : ""}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {row.natureza_operacao}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box>
                                                                    <Box mb={1}>
                                                                        <Typography variant="subtitle2" color="text.secondary">
                                                                            Emitente
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            {row.nf_nome_emissor}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography variant="subtitle2" color="text.secondary">
                                                                            Destinatário
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            {row.nf_nome_destinatario}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography variant="body1" fontWeight="medium">
                                                                    {formatarValorMonetario(row.nf_valor)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography variant="body1" fontWeight="medium">
                                                                    <Status status={row.status} />
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>

                                            <Stack direction="row" justifyContent="flex-end" mt={2}>
                                                <TablePagination
                                                    component="div"
                                                    count={total}
                                                    page={page}
                                                    onPageChange={handleChangePage}
                                                    rowsPerPage={rowsPerPage}
                                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                                    labelRowsPerPage="Linhas por página"
                                                    rowsPerPageOptions={[5, 10, 25]}
                                                />
                                            </Stack>
                                        </>
                                    )}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
                <DialogHistoricoEntradaFilter
                    open={openFiltros}
                    onClose={() => setOpenFiltros(false)}
                    onConfirm={handleApplyFilters}
                    filtrosAtuais={filtros}
                />
            </Box>
        </>
    );
}

export default function ListarHistoricoRecebimento() {
    return (
        <>
            <ScreenBanner color="#1E2E6B" title="Estoque" />
            <PermissionGuard permissionId="estoque.entrada-produtos.historico-entradas">
                <ModalDetalheHistoricoRecebimentoProvider>
                    <ListarHistoricoRecebimentoContent />
                </ModalDetalheHistoricoRecebimentoProvider>
            </PermissionGuard>
        </>
    );
}

export interface TFiltroHistoricoRecebimento {
    status?: string[]
    palavraChave?: string;
    somenteValorOperacao?: boolean;
}

const filtersChipLabels: { [key in keyof Omit<TFiltroHistoricoRecebimento, 'palavraChave'>]: string } = {
    status: "Status",
    somenteValorOperacao: "Somente valor da operação",
};